/**
 * Tipo de conexión del dispositivo, para avisar antes de gastar datos móviles.
 *
 * Usa la Network Information API (`navigator.connection`), que SOLO existe en
 * navegadores Chromium (Chrome / Edge / Opera / Samsung Internet en Android y
 * escritorio). Safari (iPhone, Mac) y Firefox no la implementan: ahí no hay
 * forma de saber si la conexión es móvil, así que `esConexionMovil` responde
 * `false` y la app NO pregunta (es mejor no preguntar que preguntar a ciegas).
 *
 * Es solo una ayuda de UX: nunca decide si una subida se hace o no por su
 * cuenta; solo dispara una confirmación que la persona puede aceptar.
 */

/** Peso sumado (bytes) por sobre el cual, con datos móviles, se pide confirmación: 50 MB. */
export const UMBRAL_AVISO_DATOS_BYTES = 50 * 1024 * 1024;

/** Clave de localStorage de «No volver a preguntar en este dispositivo». */
export const CLAVE_NO_PREGUNTAR_DATOS = "af_no_avisar_datos_moviles";

/** `effectiveType` de la API que equivale a una conexión lenta (típica de datos móviles flojos). */
const TIPOS_EFECTIVOS_LENTOS: readonly string[] = ["slow-2g", "2g", "3g"];

/** Lo poco que usamos de `NetworkInformation` (no está en los tipos de TypeScript). */
export interface InfoConexion {
  type?: string;
  effectiveType?: string;
  saveData?: boolean;
}

/** Por qué se considera «datos móviles»: sirve para redactar el aviso. */
export type MotivoDatosMoviles = "celular" | "ahorro_de_datos" | "conexion_lenta";

/** Lee `navigator.connection` si existe; `null` si no (Safari, Firefox, tests, SSR). */
export function leerConexion(nav?: unknown): InfoConexion | null {
  try {
    const n = nav ?? (typeof navigator !== "undefined" ? navigator : undefined);
    const c = (n as { connection?: unknown } | undefined)?.connection;
    return c && typeof c === "object" ? (c as InfoConexion) : null;
  } catch {
    return null;
  }
}

/**
 * Motivo por el que la conexión cuenta como «datos móviles», o `null` si no lo es o
 * no se puede saber (API ausente). Prioridad: celular > ahorro de datos > conexión lenta.
 */
export function motivoDatosMoviles(nav?: unknown): MotivoDatosMoviles | null {
  const c = leerConexion(nav);
  if (!c) return null;
  if (c.type === "cellular") return "celular";
  if (c.saveData === true) return "ahorro_de_datos";
  if (typeof c.effectiveType === "string" && TIPOS_EFECTIVOS_LENTOS.includes(c.effectiveType)) {
    return "conexion_lenta";
  }
  return null;
}

/** ¿La conexión parece de datos móviles? Sin la API responde `false`. */
export function esConexionMovil(nav?: unknown): boolean {
  return motivoDatosMoviles(nav) !== null;
}

/** ¿Hay que pedir confirmación antes de subir `bytes` con esta conexión? (estrictamente más de 50 MB) */
export function debeAvisarDatosMoviles(bytes: number, nav?: unknown): boolean {
  return Number.isFinite(bytes) && bytes > UMBRAL_AVISO_DATOS_BYTES && esConexionMovil(nav);
}

/** ¿La persona pidió no volver a ver el aviso en este dispositivo? (localStorage puede fallar) */
export function leerNoPreguntarDatos(): boolean {
  try {
    return localStorage.getItem(CLAVE_NO_PREGUNTAR_DATOS) === "1";
  } catch {
    return false;
  }
}

/** Guarda (o borra) la preferencia de no volver a preguntar. Nunca lanza. */
export function guardarNoPreguntarDatos(valor: boolean): void {
  try {
    if (valor) localStorage.setItem(CLAVE_NO_PREGUNTAR_DATOS, "1");
    else localStorage.removeItem(CLAVE_NO_PREGUNTAR_DATOS);
  } catch {
    /* modo privado o almacenamiento bloqueado: se vuelve a preguntar, no pasa nada */
  }
}
